import { store } from '../stateManager/store';
import {
  BookingStatusObject,
  CanvasConfig,
  CruiseCardRibbon,
  ExcursionIcon,
  ImageConfig,
  IOffersRibbonConfig,
  LabCDObject,
  LoginRedirect,
  PaymentInfo,
  ShipMapping,
  StrikethroughDiscountCodesObject,
  StrikethroughObject,
  Tag,
} from '@msc-b2c/interfaces/interfaces';
import { DealPromotion, DealsFilters } from '@msc-b2c/interfaces/types';
import { v4 as uuidv4 } from 'uuid';

const parseBooleanConfigValue = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number' && (value === 0 || value === 1)) return value === 1;
  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalizedValue)) return true;
    if (['false', '0', 'no', 'off'].includes(normalizedValue)) return false;
  }
  return undefined;
};

/**
 * Retrieves the currency symbol from the Redux store.
 *
 * @returns {string | undefined} The currency symbol, or undefined if not found.
 */
export const getCurrencySymbol = (): string => {
  return store.getState().genericConfigs.configs.b2cCommerceConfig.currencySymbol || '€';
};

/**
 * Retrieves the currency code from the Redux store.
 *
 * @returns {string | undefined} The currency code, or undefined if not found.
 */
export const getCurrencyCode = (): string => {
  return (
    store.getState().genericConfigs.configs.b2cCommerceConfig.currencyCode ||
    document.querySelector('meta[name="currencyCode"]')?.getAttribute('content') ||
    'EUR'
  );
};

export const getEnableDecimalOnlyIfDifferentFrom0 = (): boolean => {
  return (
    store.getState().genericConfigs.configs.b2cCommerceConfig.enableDecimalOnlyIfDifferentFrom0 ||
    false
  );
};

/**
 * Retrieves the profiling payload feature flag from context-aware config.
 * Fallbacks to meta tag to support pages where generic configs are not yet available.
 *
 * @returns {boolean} True when new profiling payload structure must be sent.
 */
export const getIsNewProfilingDataEnabled = (): boolean => {
  const b2cCommerceConfig = store.getState().genericConfigs?.configs?.b2cCommerceConfig;
  const contextAwareValue = b2cCommerceConfig?.isNewProfilingConsents;
  const parsedContextAwareValue = parseBooleanConfigValue(contextAwareValue);

  if (typeof parsedContextAwareValue === 'boolean') {
    return parsedContextAwareValue;
  }

  const metaValue = document
    .querySelector('meta[name="isNewProfilingConsents"]')
    ?.getAttribute('content');
  const parsedMetaValue = parseBooleanConfigValue(metaValue);
  return parsedMetaValue ?? false;
};

/**
 * Retrieves the decimal separator from the Redux store.
 *
 * @returns {string | undefined} The decimal separator, or undefined if not found.
 */
export const getDecimalSeparator = (): string | undefined => {
  return store.getState().genericConfigs?.configs?.b2cCommerceConfig?.decimalSeparator;
};

/**
 * Retrieves the thousand separator from the Redux store.
 *
 * @returns {string | undefined} The thousand separator, or undefined if not found.
 */
export const getThousandSeparator = (): string | undefined => {
  return store.getState().genericConfigs?.configs?.b2cCommerceConfig?.thousandsSeparator || '';
};

/**
 * Retrieves the price precision from the Redux store.
 *
 * @returns {number} The price precision, or 2 if not found.
 */
export const getPricePrecision = (): number => {
  return store.getState().genericConfigs?.configs?.b2cCommerceConfig?.pricePrecision || 2;
};

export const hasNonZeroDecimals = (number: number): boolean => {
  return number % 1 !== 0;
};

export const formatPrice = (price: string): string => {
  const [integerPart, decimalPart] = price.toString().split('.');
  const decimalSeparator = getDecimalSeparator();
  const thousandSeparator = getThousandSeparator();

  // Format the integer part with a thousand separator
  const formattedIntegerPart = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    thousandSeparator || ''
  );

  // Return formatted price, with or without decimal part
  return decimalPart && !/^[0]+$/.test(decimalPart)
    ? `${formattedIntegerPart}${decimalSeparator}${decimalPart}`
    : formattedIntegerPart;
};

/**
 * Rounds a price string to the specified precision from the Redux store.
 * Matches Java CabinService.priceFormatter() behavior:
 * - If roundUpPrices: round up to closest integer (CEILING).
 * - Else: round down to precision decimals (FLOOR).
 * - If hideDecimalsIfZero: strip trailing zeros from decimal part.
 *
 * @param {string | number | undefined} price - The price string or number to round.
 * @returns {string} - The rounded price string, or empty string on invalid input.
 */
export const roundPrice = (price: string | number | undefined): string => {
  if (price !== 0 && (price === undefined || price === null || price === '')) {
    return '';
  }

  const roundUpPrices = getRoundUpPrice();
  const precision = getPricePrecision();
  const hideDecimalsIfZero = getEnableDecimalOnlyIfDifferentFrom0();

  try {
    const priceToNumber = typeof price === 'string' ? parseFloat(price) : Number(price);
    if (Number.isNaN(priceToNumber)) {
      return '';
    }

    let value: number;
    if (roundUpPrices) {
      value = Math.ceil(priceToNumber);
    } else {
      const factor = 10 ** precision;
      value = Math.floor(priceToNumber * factor) / factor;
    }

    const scale = roundUpPrices ? 0 : precision;
    let result = value.toFixed(scale);

    // Only hide decimals when the decimal part is actually zero (e.g. 853.00 → "853")
    if (hideDecimalsIfZero && scale > 0 && !hasNonZeroDecimals(value)) {
      result = value.toString();
    }
    return result;
  } catch {
    return '';
  }
};

/**
 * Retrieves the strikethrough array from the Redux store.
 *
 * @returns {StrikethroughObject[] | undefined} The strikethrough array, or undefined if not found.
 */
export const getStrikethrough = (): StrikethroughObject[] | undefined => {
  return store.getState().genericConfigs?.configs?.b2cCommerceConfig?.strikethrough;
};

export const getRoundUpPrice = () => {
  return store.getState().genericConfigs?.configs?.cruiseSearchConfig?.roundUpPrices || false;
};

export const getCanvasItineraryName = (): CanvasConfig[] => {
  return store.getState().genericConfigs?.configs?.cruiseSearchConfig?.canvas || [];
};

export const roundUpPrice = (price: number) => {
  if (getRoundUpPrice() && hasNonZeroDecimals(price)) {
    return Number(price).toFixed(0);
  }
  return price;
};

/**
 * Retrieves the strikethrough array from the Redux store.
 *
 * @returns {StrikethroughDiscountCodesObject[] | undefined} The strikethrough array, or undefined if not found.
 */
export const getStrikethroughDiscountCodes = (): StrikethroughDiscountCodesObject[] | undefined => {
  return store.getState().genericConfigs?.configs?.b2cCommerceConfig?.strikethroughDiscountCodes;
};

/**
 * Retrieves the date format from the Redux store.
 *
 * @returns {string} The date format, or undefined if not found.
 */
export const getDateFormat = (): string => {
  return store.getState().genericConfigs?.configs?.b2cCommerceConfig?.dateFormat || 'YYYY-MM-DD';
};

/**
 * Retrieves the hour 12 format from the Redux store.
 *
 * @returns {String} The hour 12 format, or undefined if not found.
 */
export const getTimeFormat = (): string => {
  return store.getState().genericConfigs?.configs?.b2cCommerceConfig?.timeFormat || 'HH:mm';
};

/**
 * Retrieves the locale from the Redux store.
 *
 * @returns {string} The locale, or undefined if not found.
 */
export const getLocale = (): string => {
  return (
    store.getState().genericConfigs?.configs?.b2cCommerceConfig?.locale ||
    document.querySelector('meta[name="localeCode"]')?.getAttribute('content') ||
    ''
  );
};

/**
 * Retrieves the language code from the Redux store.
 *
 * @returns {string | undefined} The language code, or undefined if not found.
 */
export const getLanguageCode = (): string | undefined => {
  return (
    store.getState().genericConfigs?.configs?.b2cCommerceConfig?.languageCode ||
    document.querySelector('meta[name="languageCode"]')?.getAttribute('content') ||
    ''
  );
};

/**
 * Retrieves the language code for private area from the Redux store.
 *
 * @returns {string | undefined} The language code, or undefined if not found.
 */
export const getLanguageCode3Char = (): string => {
  return store.getState().genericConfigs?.configs?.b2cCommerceConfig?.languageCode3Char || 'eng';
};

/**
 * Retrieves the country code from the Redux store.
 *
 * @returns {string | undefined} The country code, or undefined if not found.
 */
export const getCountryCode = (): string | undefined => {
  return (
    store.getState().genericConfigs?.configs?.b2cCommerceConfig?.countryCode ||
    document.querySelector('meta[name="countryCodeIso2"]')?.getAttribute('content') ||
    ''
  );
};

/**
 * Retrieves the country code in ISO3 format from the Redux store.
 *
 * @returns {string | undefined} The country code in ISO3 format, or undefined if not found.
 */
export const getCountryCodeISO3 = (): string | undefined => {
  return store.getState().genericConfigs?.configs?.b2cCommerceConfig?.countryCodeISO3;
};

/**
 * Retrieves the conversation ID from the Redux store.
 *
 * @returns {string | undefined} The conversation ID, or undefined if not found.
 */
export const getConversationID = (): string | undefined => {
  return store.getState().genericConfigs?.configs?.conversationID || uuidv4();
};

/**
 * Retrieves the hits per page from the Redux store.
 *
 * @returns {number} The hits per page, or undefined if not found.
 */
export const getHitsPerPage = (): number => {
  return store?.getState()?.genericConfigs?.configs?.cruiseSearchConfig?.hitsPerPage || 8;
};
/**
 * Retrieves the max total number of guests from the Redux store.
 *
 * @returns {number} The max total number of guests, or undefined if not found.
 */
export const getMaxTotalNumberOfGuests = (): number => {
  return (
    store?.getState()?.genericConfigs?.configs?.cruiseSearchConfig?.maxTotalNumberOfGuests || 4
  );
};

/**
 * Retrieves the ship mapping object from the Redux store.
 *
 * @returns {object} The hits per page, or undefined if not found.
 */
export const getShipMapping = (): ShipMapping => {
  return store?.getState()?.genericConfigs?.configs?.b2cCommerceConfig?.shipMapping || {};
};

/**
 * Retrieves the language mapping from the Redux store.
 *
 * @returns {{ languageCode: string; languageLabel: string }[]} An array of objects containing language codes and their corresponding labels, or an empty array if not found.
 */
export const getLanguageMapping = (): { languageCode: string; languageLabel: string }[] => {
  return store?.getState()?.genericConfigs?.configs?.b2cCommerceConfig?.languageMapping || [];
};

/**
 * Retrieves the language mapping from the Redux store.
 *
 * @returns {{ adultAge: number; marketCode: string }[]} An array of objects containing ages of adulthood and their corresponding market code, or an empty array if not found.
 */
export const getAdultAgePerMarketMapping = (): { adultAge: number; marketCode: string }[] => {
  return (
    store?.getState()?.genericConfigs?.configs?.b2cCommerceConfig?.adultAgePerMarketMapping || []
  );
};

/**
 * Retrieves the deals filters from the Redux store.
 *
 * @returns {object} The deals filters, or undefined if not found.
 */
export const getDealsFilters = (): DealsFilters[] => {
  return store?.getState()?.dealsConfig?.dealsFilters || [];
};

/**
 * Returns true when filters from dealsFilters are editable (static SEO pages).
 * When false, filters are locked as on classic deals pages.
 */
export const getDealsFiltersEditable = (): boolean => {
  return store?.getState()?.dealsConfig?.dealsFiltersEditable ?? false;
};

/**
 * Returns filter types the user has explicitly cleared (on editable pages).
 * useDealsFilters skips re-applying these.
 */
export const getDealsFiltersUserCleared = (): string[] => {
  return store?.getState()?.dealsConfig?.dealsFiltersUserCleared ?? [];
};

/**
 * Retrieves the deals promotions list from the Redux store.
 *
 * @returns {object} The deals promotions list, or undefined if not found.
 */
export const getDealsPromotions = (): DealPromotion | null => {
  return store?.getState()?.dealsConfig?.dealsPromotion || null;
};

export const getTemplateName = (): string | null => {
  return store?.getState()?.dealsConfig?.templateName || '';
};

/**
 * Retrieves the number of days to display a document and emergency from the Redux store.
 *
 * @returns {number} The number of days to display document and emergency sections, or 0 if not found.
 */
export const getDaysToDisplayDocumentAndEmergency = (): number => {
  return (
    store?.getState()?.genericConfigs?.configs?.bookingFunnelConfig
      ?.daysToDisplayDocumentAndEmergency || 0
  );
};

/**
 * Retrieves the payment methods for each country from the Redux store.
 *
 * @returns {Array<string> | []} The payment methods array, or empty array if not found.
 */
export const getPaymentMethods = (): Array<string> => {
  return store.getState().genericConfigs.configs.bookingFunnelConfig.allowedPaymentsMethods || [];
};

export const getDepositPaymentMethods = (): Array<string> => {
  return (
    store.getState().genericConfigs.configs.bookingFunnelConfig.allowedPaymentsMethodsDeposit || []
  );
};

export const getAllowedCardNetworks = (): Array<string> => {
  return store.getState().genericConfigs.configs.bookingFunnelConfig.allowedCardNetworks || [];
};

export const getDepositAllowedCardNetworks = (): Array<string> => {
  return (
    store.getState().genericConfigs.configs.bookingFunnelConfig.allowedCardNetworksDeposit || []
  );
};

export const getPaymentButtonMerchantName = (): string => {
  return (
    store.getState().genericConfigs.configs.bookingFunnelConfig.paymentButtonMerchantName || ''
  );
};

export const getPaymentButtonAmountPaymentLabel = (): string => {
  return (
    store.getState().genericConfigs.configs.bookingFunnelConfig.paymentButtonAmountPaymentLabel ||
    ''
  );
};

export const getAllowedCardNetworksPaymentButton = (): Array<string> => {
  return (
    store.getState().genericConfigs.configs.bookingFunnelConfig.allowedCardNetworksPaymentButton ||
    []
  );
};

export const getAllowededCardNetworksForSecureFields = (): Array<string> => {
  const cards: string[] = [];
  getAllowedCardNetworks().forEach((cardNetwork) => {
    switch (cardNetwork) {
      case 'VISA':
        cards.push('VIS');
        break;
      case 'MASTERCARD':
        cards.push('ECA');
        break;
      case 'AMEX':
        cards.push('AMX');
        break;
      case 'DINERS':
        cards.push('DIN');
        break;
      default:
        break;
    }
  });

  return cards;
};

export const getDepositAllowedCardNetworksForSecureFields = (): string[] => {
  const cards: string[] = [];
  getDepositAllowedCardPaymentButtons().forEach((cardNetwork) => {
    switch (cardNetwork) {
      case 'VISA':
        cards.push('VIS');
        break;
      case 'MASTERCARD':
        cards.push('ECA');
        break;
      case 'AMEX':
        cards.push('AMX');
        break;
      case 'DINERS':
        cards.push('DIN');
        break;
      default:
        break;
    }
  });

  return cards;
};

export const getAllowedPaymentButtons = (): Array<string> => {
  return store.getState().genericConfigs.configs.bookingFunnelConfig.allowedPaymentButtons || [];
};

export const getDepositAllowedPaymentButtons = (): Array<string> => {
  return (
    store.getState().genericConfigs.configs.bookingFunnelConfig.allowedPaymentButtonsDeposit || []
  );
};

export const getDaysToHideFullPayment = (): number | undefined => {
  return (
    store.getState().genericConfigs.configs.bookingFunnelConfig.daysToHideFullPayment || undefined
  );
};

export const getEnableHoldOption = (): boolean => {
  return store.getState().genericConfigs.configs.bookingFunnelConfig.enableHoldOption;
};

export const getDaysToHideHoldOption = (): number | null => {
  return store.getState().genericConfigs.configs.bookingFunnelConfig.daysToHideHoldOption;
};

export const getOffersRibbonConfig = (): IOffersRibbonConfig[] | null => {
  return store.getState()?.genericConfigs?.configs?.bookingFunnelConfig?.offersRibbonConfig;
};

export const getActBaseImagePath = (): string => {
  return store.getState().genericConfigs.configs.bookingFunnelConfig.actImageBasePath || '';
};

export const getActPlaceholderImagePath = (): string => {
  return store.getState().genericConfigs.configs.bookingFunnelConfig.actPlaceholderImagePath || '';
};

export const getObsBaseImagePath = (): string => {
  return store.getState().genericConfigs.configs.bookingFunnelConfig.obsImageBasePath || '';
};

export const getObsPlaceholderImagePath = (): string => {
  return store.getState().genericConfigs.configs.bookingFunnelConfig.obsPlaceholderImagePath || '';
};

export const getObsBaseIconPath = (): string => {
  return store.getState().genericConfigs.configs.bookingFunnelConfig.obsIconBasePath || '';
};

export const getObsPlaceholderIconPath = (): string => {
  return store.getState().genericConfigs.configs.bookingFunnelConfig.obsPlaceholderIconPath || '';
};

export const getCountryCodeFromMetaTag = (): string => {
  return document.querySelector('meta[name="countryCodeIso2"]')?.getAttribute('content') || 'GB';
};

export const getCountryCodeISO3FromMetaTag = (): string => {
  return document.querySelector('meta[name="countryCodeIso3"]')?.getAttribute('content') || 'GBR';
};

export const getBaseURL = (): string => {
  return document.querySelector('meta[name="baseURL"]')?.getAttribute('content') || '';
};

/* TODO: Remove this logic after this ticket is done: DTTP25-27161 */
export const isProduction = (): boolean => {
  return (
    document.querySelector('meta[name="envType"]')?.getAttribute('content')?.toLowerCase() ===
      'prod' || false
  );
};

export const isChain2 = (): boolean => {
  const envType = document
    .querySelector('meta[name="envType"]')
    ?.getAttribute('content')
    ?.toLowerCase();
  return envType === 'dev' || envType === 'dev2' || false;
};

/* END of remove*/

/**
 * Retrieves the itinerary images configured by itinerary code from the Redux store.
 *
 * @returns {string | undefined} The currency symbol, or undefined if not found.
 */
export const getItineraryCodesConfiguredImages = (): { [key: string]: ImageConfig[] } => {
  return (
    store.getState().genericConfigs.configs.cruiseSearchConfig.itineraryImages
      .itineraryCodesImagesConfig || {}
  );
};

/**
 * Retrieves the itinerary images configured by area from the Redux store.
 *
 * @returns {string | undefined} The currency symbol, or undefined if not found.
 */
export const getAreaConfiguredImages = (): { [key: string]: ImageConfig[] } => {
  return (
    store.getState().genericConfigs.configs.cruiseSearchConfig.itineraryImages.areaImagesConfig ||
    {}
  );
};

/**
 * Retrieves the itinerary fallback images configured from the Redux store.
 *
 * @returns {string | undefined} The currency symbol, or undefined if not found.
 */
export const getFallbackImages = (): ImageConfig[] => {
  return (
    store.getState().genericConfigs.configs.cruiseSearchConfig.itineraryImages
      .fallbackImagesConfig || []
  );
};

export const getItineraryImgDomain = (): string => {
  return store.getState().genericConfigs.configs.b2cCommerceConfig.itineraryImgDomain || '';
};

export const isEmbarkAndDisembarkExcursionsEnabled = (): boolean => {
  return (
    store.getState().genericConfigs.configs.b2cCommerceConfig.enableEmbarkAndDisembarkExcursions ||
    false
  );
};

export const getPreventDownload = (): boolean => {
  return store.getState().genericConfigs?.configs?.b2cCommerceConfig?.preventDownload ?? false;
};

export const getEditorialCarouselNextIconLabel = (): string => {
  return (
    store.getState().genericConfigs.configs.editorialComponentsConfig?.carouselNextIconLabel || ''
  );
};

export const getEditorialCarouselPrevIconLabel = (): string => {
  return (
    store.getState().genericConfigs.configs.editorialComponentsConfig?.carouselPrevIconLabel || ''
  );
};

export const getEditorialAccordionOpenIconLabel = (): string => {
  return (
    store.getState().genericConfigs.configs.editorialComponentsConfig?.accordionOpenIconLabel || ''
  );
};

export const getEditorialAccordionCloseIconLabel = (): string => {
  return (
    store.getState().genericConfigs.configs.editorialComponentsConfig?.accordionCloseIconLabel || ''
  );
};

export const getOverwriteExistingLabelsForCarouselLabels = (): boolean => {
  return (
    store.getState().genericConfigs.configs.editorialComponentsConfig
      ?.overwriteExistingLabelsForCarouselLabels || false
  );
};

export const getOverwriteExistingLabelsForAccordionLabels = (): boolean => {
  return (
    store.getState().genericConfigs.configs.editorialComponentsConfig
      ?.overwriteExistingLabelsForAccordionLabels || false
  );
};

export const getOverwriteWishlistAutorTooltip = (): boolean => {
  return (
    store.getState().genericConfigs.configs.editorialComponentsConfig
      ?.overwriteWishlistAutorTooltip || false
  );
};

export const getWishlistIconTooltipWhenAdded = (): string => {
  return (
    store.getState().genericConfigs.configs.editorialComponentsConfig
      ?.wishlistIconTooltipWhenAdded || ''
  );
};

export const getWishlistIconTooltipWhenNotAdded = (): string => {
  return (
    store.getState().genericConfigs.configs.editorialComponentsConfig
      ?.wishlistIconTooltipWhenNotAdded || ''
  );
};

export const getFilterTags = (): Tag[] => {
  return store.getState().genericConfigs.configs.cruiseSearchConfig.tags || [];
};

export const getRibbonsConfig = (): CruiseCardRibbon[] => {
  return store.getState().genericConfigs.configs.cruiseSearchConfig.ribbons || [];
};

export const getMyMscAreaBasePath = (): string => {
  return store.getState().genericConfigs.configs.myPrivateAreaConfig.myMscAreaBasePath || '';
};

/**
 * Return the homepage of MY MSC to be redirect when the login is successful
 * @returns string
 */
export const getHomePageMyMSC = (): string => {
  return store.getState().genericConfigs.configs.myPrivateAreaConfig.myPrivateAreaHomepage || '';
};

/**
 * Retrieves the number of days to disable light login or hide prepaid from the Redux store.
 *
 * @returns {number | null} The number of days to disable light login or hide prepaid, or 0 if not found.
 */
export const getDaysToDisableLightLoginOrHidePrepaid = (): number | null => {
  return (
    store.getState().genericConfigs.configs.myPrivateAreaConfig
      .daysToDisableLightLoginOrHidePrepaid || 0
  );
};

/**
 * Return the list of the possibility redirect after the login
 * @returns {LoginRedirect[] | []}
 */
export const getLoginRedirects = (): LoginRedirect[] => {
  return store.getState().genericConfigs.configs.myPrivateAreaConfig?.loginRedirects || [];
};

/**
 * Return the list of pricetypes that allow to go forward with the light login
 * @returns {string[] | []}
 */
export const getForbiddenPriceTypes = (): string[] => {
  return (
    store.getState().genericConfigs.configs.myPrivateAreaConfig.lightLoginForbiddenPriceTypes || []
  );
};

/**
 * Retrieves the Lab CD configuration from the Redux store.
 *
 * @returns {LabCDObject[]} The Lab CD configuration array, or an empty array if not found.
 */
export const getLabCDConfig = (): LabCDObject[] => {
  return store.getState().genericConfigs.configs.myPrivateAreaConfig.labCDLabels || [];
};

/**
 * Retrieves the list of not allowed price types from the Redux store.
 *
 * @returns {string[]} The list of not allowed price types, or an empty array if not found.
 */
export const getNotAllowedPriceTypes = (): string[] => {
  return store.getState().genericConfigs.configs.myPrivateAreaConfig.notAllowedPriceTypes || [];
};

export const getBookingStatusConfig = (): BookingStatusObject[] => {
  return store.getState().genericConfigs.configs.myPrivateAreaConfig.bookingStatusLabels || [];
};

/* Formats a string for use as an AEM path:
 * - Converts to lowercase
 * - Replaces '&' with 'and'
 * - Replaces '.' with '-'
 * - Removes parentheses
 * - Replaces spaces with hyphens
 *
 * @param {string} value - The string to format
 * @returns {string} The formatted AEM path string
 */
export const formatStringForAemPath = (value: string): string => {
  return value
    .toLowerCase()
    .replace(/&/g, 'and') // Replace & with 'and'
    .replace(/\./g, '-') // Replace . with -
    .replace(/[()]/g, '') // Remove parentheses
    .replace(/\s+/g, '-'); // Replace spaces with -
};

/**
 * Retrieves the configured order for displaying price and currency.
 *
 * The returned string uses the placeholders `{currency}` and `{price}`
 * to specify their display order. For example, `"{currency}{price}"` means
 * the currency appears before the price.
 *
 * If no configuration is found, it defaults to `"{currency}{price}"`.
 *
 * @returns A string defining the order of currency and price display.
 */
export const getPriceCurrencyOrder = (): string => {
  return (
    store.getState().genericConfigs.configs.b2cCommerceConfig.priceCurrencyOrder ||
    '{currency}{price}'
  );
};

/**
 * Retrieves dictionary labels from the Redux store and converts them into a key-value map.
 *
 * Each key in the returned object corresponds to a `typeLabel` from the dictionary,
 * and each value is the corresponding `placeholderLabel`.
 *
 * The function merges the fetched dictionary with a default dictionary to ensure all expected keys are present.
 *
 * Example input:
 * [
 *   { typeLabel: "Adult", placeholderLabel: "Adult || Adults" },
 *   { typeLabel: "cabin", placeholderLabel: "Cabin || Cabins" }
 * ]
 *
 * Example output:
 * {
 *   Adult: "Adult || Adults",
 *   cabin: "Cabin || Cabins",
 *   minor: "Minor||Minors",
 *   infant: "Infant||Infants",
 *   children: "Children||Children",
 *   deck: "Deck||Decks",
 *   night: "Night||Nights"
 * }
 *
 * @returns {Record<string, string>} A dictionary where keys are typeLabels and values are placeholderLabels.
 * Returns a default dictionary if the store dictionary is missing or empty.
 */

export const getDictionaryLabels = (): Record<string, string> => {
  const dictionaryArray =
    store.getState().genericConfigs.configs.b2cCommerceConfig.dictionary || [];
  const dictionaryDefault: Record<string, string> = {
    cabin: 'Cabin||Cabins',
    minor: 'Minor||Minors',
    infant: 'Infant||Infants',
    adult: 'Adult||Adults',
    children: 'Children||Children',
    deck: 'Deck||Decks',
    night: 'Night||Nights',
    passenger: 'Passenger||Passengers',
    aft: 'Aft||Afts',
    middleShip: 'Middle-Ship||Middle-Ships',
    midShip: 'Mid-Ship||Mid-Ships',
    middle: 'Middle||Middles',
    mid: 'Mid||Mids',
    front: 'Front||Fronts',
    forward: 'Forward||Forwards',
    guaranteeCabin: 'Guarantee Cabin||Guarantee Cabins',
    PM: 'PM||PMs',
    AM: 'AM||AMs',
  };

  const dictionaryUpdate = dictionaryArray.reduce<Record<string, string>>((acc, item) => {
    const key = `${item.typeLabel}`;
    acc[key] = item.placeholderLabel;
    return acc;
  }, {});

  for (const key in dictionaryDefault) {
    if (!(key in dictionaryUpdate)) {
      dictionaryUpdate[key] = dictionaryDefault[key];
    }
  }
  return dictionaryUpdate;
};

export const getSearchDaysBeforeDeparture = (): number => {
  return store.getState().genericConfigs.configs.cruiseSearchConfig.searchDaysBeforeDeparture || 0;
};

export const getHideHotelServiceCharges = (): boolean => {
  return !!store.getState().genericConfigs.configs.bookingFunnelConfig.hideHotelServiceCharges;
};

export const getHidePortTaxes = (): boolean => {
  return !!store.getState().genericConfigs.configs.bookingFunnelConfig.hidePortTaxes;
};

/**
 * Whether to send paymentMethod, email and phone in the prices API (Klarna).
 * When false, those fields are not sent (backend not ready yet).
 */
export const isEnableApiPaymentKlarna = (): boolean => {
  return !!store.getState().genericConfigs.configs.bookingFunnelConfig.enableApiPaymentKlarna;
};

/**
 * MyMSC: when true, pre-fills the lead guest from customer dataByLogin; when false, skips that request (context-aware config).
 */
export const isEnableDataByLoginPrefill = (): boolean => {
  return !!store.getState().genericConfigs.configs.bookingFunnelConfig.enableDataByLoginPrefill;
};

/**
 * Retrieves the type of site if is partnership (partnership) o b2cw (b2c)
 *
 * @returns {string} The type of site defaults b2cw
 */
export const getSiteType = (): string | undefined => {
  return (
    store.getState().genericConfigs?.configs?.siteTypeConfig?.siteType ||
    document.querySelector('meta[name="siteType"]')?.getAttribute('content')?.toLowerCase() ||
    'b2c'
  );
};

/**
 * Return if the site is partnership
 * @returns {boolean} true|false
 */
export const isPartnership = (): boolean => {
  return getSiteType() === 'partnership';
};

/**
 * Return if the site is b2c
 * @returns {boolean} true|false
 */
export const isB2C = (): boolean => {
  return getSiteType() === 'b2c';
};

/**
 * Checks if the site type corresponds to the "My MSC Area".
 *
 * @returns {boolean} `true` if the site type is `b2c-my-msc-area`, otherwise `false`.
 */
export const isMyMscArea = (): boolean => {
  return getSiteType() === 'b2c-my-msc-area';
};

/**
 * Returns the Content Fragment (CF) folder path based on the application configuration.
 *
 * - Checks the B2C commerce configuration from the store for a country-specific CF folder path.
 * - If not set and the app is in a partnership context (`isPartnership()` returns true),
 *   it defaults to the partnership CF folder path.
 * - Otherwise, defaults to the general B2C website CF folder path.
 *
 * @returns {string} The resolved CF folder path.
 */
export const getCFFolderPath = (): string => {
  let cfFolderPath =
    store.getState().genericConfigs.configs.b2cCommerceConfig?.contentFragmentInfo
      ?.contentFragmentsRoot || '/content/dam';
  if (
    (cfFolderPath === '' || cfFolderPath === null || cfFolderPath === undefined) &&
    isPartnership()
  ) {
    cfFolderPath = '/content/dam/partnership/content-fragments';
  } else if (
    (cfFolderPath === '' || cfFolderPath === null || cfFolderPath === undefined) &&
    (isB2C() || isMyMscArea())
  ) {
    cfFolderPath = '/content/dam/b2c-website-foundation/content-fragments';
  }

  return cfFolderPath;
};

/**
 * Retrieves the list of not allowed agency code, from the Redux store.
 *
 * @returns {string[]} The list of not allowed agency code, or an empty array if not found.
 */
export const getNotAllowedAgencyCode = (): string[] => {
  return (
    store.getState().genericConfigs.configs.myPrivateAreaConfig.lightLoginForbiddenAgencyCode || []
  );
};

/**
 *
 * @returns {ExcursionIcon[]} the list of excusursion icons [{"activityCode":"xxx","activityIcon":"/content/dam/assets.jpg"}]
 *
 */
export const getExursionsActivityIcons = (): ExcursionIcon[] => {
  return store.getState().genericConfigs.configs.myPrivateAreaConfig.excursionActivityToIcon || [];
};

/**
 * Retrieves the WCI opens on date from the Redux store.
 *
 * @returns {number | undefined} The WCI opens on date, or undefined if not found.
 */
export const getWciOpensOn = (): number | undefined => {
  return store.getState().genericConfigs.configs.myPrivateAreaConfig.wciOpensOn;
};

/**
 * Retrieves the WCI closes on date from the Redux store.
 *
 * @returns {number | undefined} The WCI closes on date, or undefined if not found.
 */
export const getWciClosesOn = (): number | undefined => {
  return store.getState().genericConfigs.configs.myPrivateAreaConfig.wciClosesOn;
};

/**
 * Retrieves the threshold days from the Redux store.
 *
 * @returns {number | undefined} The threshold if not found.
 */
export const getPromoThreshold = (): number | undefined => {
  return store.getState().genericConfigs.configs.myPrivateAreaConfig.promoThreshold;
};

/**
 * Retrieves the configured age threshold for small kids from the Redux store.
 * If not set, it defaults to 3.
 *
 * @returns {number} The age threshold value in years
 */
export const getSmallKidAgeThreshold = (): number => {
  return store.getState().genericConfigs.configs.myPrivateAreaConfig.smallKidAgeThreshold || 3;
};

/**
 * Retrieves the minimum age required for a passenger for light login.
 * This value is fetched from the application's state, specifically from
 * `myPrivateAreaConfig.lightLoginPassengerMinimumAge`. If not set, it defaults to 18.
 *
 * @returns {number} The minimum age required for a passenger for light login.
 */
export const getLightLoginPassengerMinimumAge = (): number => {
  return (
    store.getState().genericConfigs.configs.myPrivateAreaConfig.lightLoginPassengerMinimumAge || 18
  );
};

/**
 * Retrieves the list insurance allowed europe, from the Redux store.
 *
 * @returns {string[]} The list insurance allowed europe, or an empty array if not found.
 */
export const getInsuranceEurope = (): string[] => {
  return store.getState().genericConfigs.configs.myPrivateAreaConfig.insuranceEurope || [];
};

/** PAYMENT CONFIGURATION - START **/

/**
 * Retrieves the list of credit cards available for payments from the Redux store.
 *
 * @returns {PaymentInfo[]} An array of credit card objects, or an empty array if not found.
 */
export const getCreditCards = (): PaymentInfo[] => {
  return store.getState().genericConfigs.configs.b2cCommerceConfig.payments?.creditCards || [];
};

/**
 * Retrieves the list of payment buttons available for payments from the Redux store.
 *
 * @returns {PaymentInfo[]} An array of payment button objects, or an empty array if not found.
 */
export const getPaymentButtons = (): PaymentInfo[] => {
  return store.getState().genericConfigs.configs.b2cCommerceConfig.payments?.paymentButtons || [];
};

/**
 * Retrieves the list of other payment methods available from the Redux store.
 *
 * @returns {PaymentInfo[]} An array of other payment method objects (e.g. PayPal), or an empty array if not found.
 */
export const getOtherPaymentMethods = (): PaymentInfo[] => {
  return (
    store.getState().genericConfigs.configs.b2cCommerceConfig.payments?.otherPaymentMethods || []
  );
};

/**
 * Retrieves the card CVV image URL from the Redux store configuration.
 *
 * This function accesses the Redux store state to get the `cardCvvImage`
 * from the `b2cCommerceConfig` payment settings. If the image URL is not
 * set or unavailable, it returns an empty string.
 *
 * @returns {string} The URL of the card CVV image, or an empty string if not found.
 */
export const getCardCvvImage = (): string => {
  return store.getState().genericConfigs.configs.b2cCommerceConfig.payments?.cardCvvImage || '';
};

/**
 * Checks if Google Pay is enabled in the Redux store configuration.
 *
 * @returns {boolean} `true` if Google Pay is enabled, otherwise `false`.
 */
export const isGooglePayEnabled = (): boolean => {
  return (
    store.getState().genericConfigs.configs.b2cCommerceConfig.payments?.isGooglePayEnable || false
  );
};

/**
 * Checks if Apple Pay is enabled in the Redux store configuration.
 *
 * @returns {boolean} `true` if Apple Pay is enabled, otherwise `false`.
 */
export const isApplePayEnabled = (): boolean => {
  return (
    store.getState().genericConfigs.configs.b2cCommerceConfig.payments?.isApplePayEnable || false
  );
};

/**
 * Checks if PayPal is enabled in the Redux store configuration.
 *
 * @returns {boolean} `true` if PayPal is enabled, otherwise `false`.
 */
export const isPayPalEnabled = (): boolean => {
  return (
    store.getState().genericConfigs.configs.b2cCommerceConfig.payments?.isPayPalEnable || false
  );
};

/**
 * Retrieves the list of allowed card payment buttons from the Redux store configuration.
 *
 * @returns {string[]} An array of allowed card network identifiers, or an empty array if not found.
 */
export const getAllowedCardPaymentButtons = (): string[] => {
  return (
    store.getState().genericConfigs.configs.b2cCommerceConfig.payments?.allowedCardNetworks || []
  );
};

export const getDepositAllowedCardPaymentButtons = (): string[] => {
  return getAllowedCardPaymentButtons();
};

/** PAYMENT CONFIGURATION - END **/

/**
 * Retrieves the list of countries that do not use postal codes.
 *
 * This function accesses the Redux store to get the configuration for
 * countries where postal codes are not being used for area identification.
 *
 * If no configuration is found, it defaults to an empty array.
 *
 * @returns {string[]} An array of country codes in ISO-2 format (ex. [GB, IE, ...]) that do not use postal codes, or an empty array if not found.
 */
export const getNoPostCodeCountries = (): string[] => {
  return store.getState().genericConfigs.configs.b2cCommerceConfig.noPostCodeCountries || [];
};

/**
 * Returns the cookie compliance JSON string from b2cCommerceConfig (store).
 * Used for storage-compliance key config. Fallback to empty array if not set.
 */
export const getComplianceCookies = (): string => {
  const json = store.getState().genericConfigs.configs.b2cCommerceConfig.cookieComplianceJson;
  return json && json.trim() ? json : '[]';
};

/**
 * Returns the strictly necessary cookie keys from b2cCommerceConfig (store).
 * Used for storage-compliance to treat these keys as always allowed.
 */
export const getStrictlyNecessary = (): string[] => {
  return store.getState().genericConfigs.configs.b2cCommerceConfig.strictlyNecessary ?? [];
};

/**
 * When `false`, `mscGet` / `mscSet` / `mscRemove` ignore `window.__mscStorage` and use native
 * Web Storage only (legacy behavior). Storage-compliance init also skips patching until this is `true`.
 */
export const getCookieComplianceEnabled = (): boolean => {
  return store.getState().genericConfigs.configs.b2cCommerceConfig.cookieComplianceEnabled ?? false;
};
